import { useCallback, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePlatforms, type Platform } from '@mint-lib/routing-context';
import {
  Chip,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import type { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom } from 'jotai';

import { selectedSuggestionAtom } from '../../../store/index.js';

const INTEGRATION_TYPES = ['one_way', 'two_way', 'virtual'] as const;

const AcceptedPlatforms = () => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const [selectedSuggestion] = useAtom(selectedSuggestionAtom);

  const { platformByLabel } = usePlatforms();

  const categorizePlatforms = useCallback(
    (items: Suggestions.TaxonomyChildrenType[]) => {
      const manuallyApplied: Platform[] = [];
      const automaticApplied: Platform[] = [];
      const notAffectedPlatforms: string[] = [];

      const traverse = (item: Suggestions.TaxonomyChildrenType) => {
        if (item.type === 'platform') {
          const currentPlatform = platformByLabel[item.name]; // Can be undefined when platform is not found or renamed

          if (!currentPlatform) {
            notAffectedPlatforms.push(item.name);
          }

          if (currentPlatform?.isIntegrated) {
            automaticApplied.push(currentPlatform);
          }

          if (INTEGRATION_TYPES.includes(currentPlatform?.type)) {
            manuallyApplied.push(currentPlatform);
          }
        }

        if (item.children) {
          item.children.forEach((child) => traverse(child));
        }
      };

      items.forEach((item) => traverse(item));

      return { manuallyApplied, automaticApplied, notAffectedPlatforms };
    },
    [platformByLabel],
  );

  const { manuallyApplied, automaticApplied, notAffectedPlatforms } = useMemo(
    () => categorizePlatforms(selectedSuggestion?.taxonomy.children ?? []),
    [selectedSuggestion?.taxonomy.children, categorizePlatforms],
  );

  return (
    <>
      <p className={cn(typographyVariants({ variant: 'h4' }), 'mb-4')}>
        {t('Suggested platforms recap')}
      </p>
      <p className={typographyVariants({ variant: 'overline1' })}>
        {t('Automatic applied platforms')}
      </p>
      <div className="mt-2 mb-4 flex flex-row flex-wrap gap-4">
        {!automaticApplied.length ? (
          <p className={typographyVariants({ variant: 'bodyShort02' })}>
            {t('The platform list is not available for now')}
          </p>
        ) : (
          automaticApplied.map((platform) => (
            <Chip
              key={platform.id}
              className="flex flex-row gap-1"
              variant="outline"
              size="medium"
              color="silver"
            >
              {platform.logo && (
                <img
                  src={platform.logo ?? ''}
                  alt="Platform icon"
                  width="18px"
                  height="18px"
                />
              )}
              {platform.label}
            </Chip>
          ))
        )}
      </div>
      <p className={typographyVariants({ variant: 'overline1' })}>
        {t('Manually applied platform')}
      </p>
      <div className="mt-2 mb-4 flex flex-row flex-wrap gap-4">
        {!manuallyApplied.length ? (
          <p className={typographyVariants({ variant: 'bodyShort02' })}>
            {t('The platform list is not available for now')}
          </p>
        ) : (
          manuallyApplied.map((platform) => (
            <Chip
              className="max-w-50"
              key={platform.id}
              variant="outline"
              size="medium"
              color="silver"
            >
              <TruncateText>{platform.label}</TruncateText>
            </Chip>
          ))
        )}
      </div>
      {!!notAffectedPlatforms.length && (
        <>
          <p className={typographyVariants({ variant: 'overline1' })}>
            {t('Not affected platforms')}
          </p>
          <div className="mt-2 mb-4 flex flex-row flex-wrap gap-4">
            {notAffectedPlatforms.map((platform) => (
              <Chip
                className="max-w-50"
                key={platform}
                variant="outline"
                size="medium"
                color="silver"
              >
                <TruncateText>{platform}</TruncateText>
              </Chip>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default AcceptedPlatforms;
